import { Fragment, type ReactNode } from 'react';

import { Separator } from '@/components/ui/separator';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { cn } from '@/lib/utils';

interface KeyValueItem {
  label: string;
  value: ReactNode;
  /** Span full width (2 columns) */
  fullWidth?: boolean;
}

interface KeyValueListProps {
  items: KeyValueItem[];
  /** Number of columns */
  columns?: 1 | 2 | 3 | 4;
  /** Optional section dividers between groups */
  sections?: { afterIndex: number; label?: string }[];
  /** Display key left, value right on same row */
  inline?: boolean;
  /** Bold the values */
  boldValues?: boolean;
  /** Text size for inline mode */
  size?: 'default' | 'lg';
}

const gridColsMap = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
} as const;

export function KeyValueList({
  items,
  columns = 2,
  sections = [],
  inline = false,
  boldValues = false,
  size = 'default',
}: KeyValueListProps) {
  const sectionIndices = new Set(sections.map((s) => s.afterIndex));
  const sectionLabels = Object.fromEntries(
    sections.filter((s) => s.label).map((s) => [s.afterIndex, s.label])
  );

  if (inline) {
    return (
      <dl className="space-y-1.5">
        {items.map((item, index) => (
          <Fragment key={item.label}>
            {sectionIndices.has(index) && (
              <div className="pt-2">
                {sectionLabels[index] ? (
                  <div className="flex items-center gap-2">
                    <Separator className="flex-1" />
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      {sectionLabels[index]}
                    </span>
                    <Separator className="flex-1" />
                  </div>
                ) : (
                  <Separator />
                )}
              </div>
            )}
            <div className="flex items-baseline justify-between gap-4">
              <dt className={cn(size === 'lg' ? 'text-sm' : 'text-xs', 'text-muted-foreground')}>
                {item.label}
              </dt>
              <dd
                className={cn(
                  size === 'lg' ? 'text-base' : 'text-sm',
                  'text-right',
                  boldValues && 'font-semibold'
                )}
              >
                {renderValue(item.value)}
              </dd>
            </div>
          </Fragment>
        ))}
      </dl>
    );
  }

  return (
    <dl className={cn('grid grid-cols-1 gap-x-4 gap-y-3', gridColsMap[columns])}>
      {items.map((item, index) => (
        <Fragment key={item.label}>
          {sectionIndices.has(index) && (
            <div className={cn('pt-2', columns > 1 && 'sm:col-span-full')}>
              {sectionLabels[index] ? (
                <div className="flex items-center gap-2">
                  <Separator className="flex-1" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {sectionLabels[index]}
                  </span>
                  <Separator className="flex-1" />
                </div>
              ) : (
                <Separator />
              )}
            </div>
          )}
          <div className={cn(item.fullWidth && columns > 1 && 'sm:col-span-full')}>
            <dt className="text-xs font-medium text-muted-foreground">{item.label}</dt>
            <dd className={cn('mt-0.5 text-sm', boldValues && 'font-semibold')}>
              {renderValue(item.value)}
            </dd>
          </div>
        </Fragment>
      ))}
    </dl>
  );
}

function renderValue(value: ReactNode) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  if (typeof value === 'string') {
    return <TruncatedTooltip text={value} className="max-w-full" />;
  }

  return value;
}
